import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Sparkles, Check, Loader2, X, AlertCircle, ShieldCheck, Pencil,
} from "lucide-react";
import { toast } from "sonner";

/**
 * <DocIntelligencePanel/> — back-office review of AI-extracted fields.
 *
 * Renders inside a `<details>`-style card under each upload row. Shows:
 *   • the extracted fields (editable inputs)
 *   • confidence badge
 *   • "Accept all" → POSTs /admin/uploads/{id}/accept-extraction
 *
 * Safety: the back-office is the final authority — values are never written
 * to `matter.entities` until Accept is clicked.
 */
export default function DocIntelligencePanel({ uploadId, documentType, onAccepted }) {
  const [data, setData] = useState(null);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/uploads/${uploadId}/extraction`);
        if (cancelled) return;
        setData(data);
        const extracted = data.extracted_data?.fields || {};
        setFields(extracted);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uploadId]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/uploads/${uploadId}/accept-extraction`, { fields });
      toast.success("Extraction accepted — matter updated");
      onAccepted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full text-xs text-zinc-500 italic px-4 py-3 flex items-center gap-2">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading extraction…
      </div>
    );
  }

  if (!data) return null;

  const accepted = data.extraction_status === "accepted";
  const confidence = data.extracted_data?.confidence || "medium";
  const isLowConf = confidence === "low";
  const fieldEntries = Object.entries(fields);
  // Completeness % — proportion of fields with a non-null value.
  const filled = fieldEntries.filter(([, v]) =>
    Array.isArray(v) ? v.length > 0 : !!(v ?? "").toString().trim(),
  ).length;
  const completenessPct = fieldEntries.length
    ? Math.round((filled / fieldEntries.length) * 100)
    : 0;
  const missingKeys = fieldEntries
    .filter(([, v]) => Array.isArray(v) ? v.length === 0 : !(v ?? "").toString().trim())
    .map(([k]) => k);
  const grouped = groupFieldsByCategory(documentType, fieldEntries);

  return (
    <div className={`w-full border-t border-zinc-200 ${accepted ? "bg-emerald-50/50" : "bg-zinc-50/60"}`}
         data-testid={`doc-intel-panel-${uploadId}`}>
      <div className={`px-4 py-2.5 flex items-center justify-between gap-2 flex-wrap border-b border-zinc-200 ${
        isLowConf && !accepted ? "bg-red-50" : ""
      }`}>
        <div className="flex items-center gap-2 flex-wrap">
          <Sparkles className="h-3.5 w-3.5 text-[#0055FF]" />
          <span className="text-overline text-zinc-700">
            Extracted from document (AI)
          </span>
          {accepted ? (
            <span className="text-[10px] tracking-widest uppercase bg-emerald-100 text-emerald-800 px-1.5 py-0.5">
              Accepted
            </span>
          ) : (
            <>
              <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 ${confidencePill(confidence)}`}
                    data-testid={`doc-intel-confidence-${uploadId}`}>
                Confidence: {confidence}
              </span>
              <span className="text-[10px] tracking-widest uppercase bg-zinc-100 text-zinc-700 px-1.5 py-0.5"
                    data-testid={`doc-intel-completeness-${uploadId}`}>
                {completenessPct}% extracted ({filled}/{fieldEntries.length})
              </span>
              {isLowConf && (
                <span className="text-[10px] tracking-widest uppercase bg-red-600 text-white px-1.5 py-0.5 inline-flex items-center gap-1">
                  <AlertCircle className="h-2.5 w-2.5" /> Review carefully
                </span>
              )}
            </>
          )}
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          className="text-xs text-zinc-500 hover:text-zinc-900 underline"
        >
          {open ? "Hide" : "Show"}
        </button>
      </div>

      {open && (
        <div className="px-4 py-3">
          {Object.keys(fields).length === 0 ? (
            <div className="text-xs text-zinc-500 italic flex items-center gap-2">
              <AlertCircle className="h-3 w-3" />
              No fields extracted from this document.
            </div>
          ) : (
            <div className="space-y-4">
              {grouped.map(([category, entries]) => (
                <div key={category} data-testid={`doc-intel-group-${category}`}>
                  <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-2 flex items-center gap-2">
                    {CATEGORY_LABELS[category] || category}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {entries.map(([k, v]) => (
                      <FieldEditor
                        key={k}
                        name={k}
                        value={v}
                        emphasise={isLowConf}
                        onChange={(nv) => setFields((f) => ({ ...f, [k]: nv }))}
                        disabled={accepted}
                      />
                    ))}
                  </div>
                </div>
              ))}
              {missingKeys.length > 0 && !accepted && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 mt-2"
                     data-testid={`doc-intel-missing-${uploadId}`}>
                  Missing values: {missingKeys.map(fieldLabel).join(", ")}.
                  These were not legible to the AI — fill them manually before accepting.
                </div>
              )}
            </div>
          )}
          {!accepted && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
              <ShieldCheck className="h-3 w-3 text-zinc-400" />
              You are the final authority — edit any field before accepting.
              <div className="ml-auto flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none"
                  onClick={() => onAccepted?.(null)}
                  data-testid={`doc-intel-dismiss-${uploadId}`}
                >
                  <X className="h-3 w-3 mr-1" /> Dismiss
                </Button>
                <Button
                  size="sm"
                  onClick={accept}
                  disabled={busy || Object.keys(fields).length === 0}
                  className="rounded-none bg-[#0055FF] hover:bg-[#003ECC] text-white"
                  data-testid={`doc-intel-accept-${uploadId}`}
                >
                  {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                  Accept all
                </Button>
              </div>
            </div>
          )}
          {accepted && (
            <div className="mt-2 text-xs text-emerald-700 flex items-center gap-2">
              <Check className="h-3 w-3" /> Accepted by {data.accepted_by || "back-office"}
              {data.accepted_at && ` · ${new Date(data.accepted_at).toLocaleString("en-ZA")}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function confidencePill(level) {
  if (level === "high") return "bg-emerald-100 text-emerald-800";
  if (level === "low")  return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-900";
}

// Field grouping by category — identity / financial / legal / other.
const CATEGORY_LABELS = {
  identity:        "Identity",
  financial:       "Financial",
  legal_structure: "Legal structure",
  other:           "Other",
};

const FIELD_CATEGORY = {
  // Identity
  full_name:           "identity",
  testator_full_name:  "identity",
  executor_full_name:  "identity",
  founder_full_name:   "identity",
  deceased_full_name:  "identity",
  informant_full_name: "identity",
  id_number:           "identity",
  testator_id_number:  "identity",
  executor_id_number:  "identity",
  founder_id_number:   "identity",
  deceased_id_number:  "identity",
  date_of_birth:       "identity",
  gender:              "identity",
  citizenship:         "identity",
  id_type:             "identity",
  residential_address: "identity",
  // Financial
  purchase_price:      "financial",
  deposit:             "financial",
  bond_amount:         "financial",
  bond_bank:           "financial",
  // Legal structure
  trust_name:          "legal_structure",
  trust_purpose:       "legal_structure",
  date_signed:         "legal_structure",
  date_of_death:       "legal_structure",
  place_of_death:      "legal_structure",
  cause_of_death:      "legal_structure",
  certificate_number:  "legal_structure",
  witnesses:           "legal_structure",
  trustees:            "legal_structure",
  beneficiaries:       "legal_structure",
  type_of_proof:       "legal_structure",
  document_date:       "legal_structure",
  issuer:              "legal_structure",
};

function groupFieldsByCategory(documentType, entries) {
  const groups = { identity: [], financial: [], legal_structure: [], other: [] };
  entries.forEach(([k, v]) => {
    const cat = FIELD_CATEGORY[k] || "other";
    groups[cat].push([k, v]);
  });
  // Return only non-empty groups in their canonical order.
  return Object.entries(groups).filter(([, items]) => items.length > 0);
}

// ---------------------------------------------------------------------------

function FieldEditor({ name, value, onChange, disabled, emphasise }) {
  const inputClass = `mt-1 w-full text-sm px-2 py-1.5 border disabled:bg-zinc-100 ${
    emphasise ? "border-red-300 bg-red-50/30" : "border-zinc-300"
  }`;
  if (Array.isArray(value)) {
    return (
      <label className="block text-xs">
        <span className="text-zinc-600 capitalize flex items-center gap-1">
          <Pencil className="h-2.5 w-2.5" /> {fieldLabel(name)} (list)
        </span>
        <textarea
          rows={Math.max(2, value.length)}
          value={value.join("\n")}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))}
          className={inputClass}
          data-testid={`doc-intel-field-${name}`}
        />
      </label>
    );
  }
  return (
    <label className="block text-xs">
      <span className="text-zinc-600 flex items-center gap-1">
        <Pencil className="h-2.5 w-2.5" /> {fieldLabel(name)}
      </span>
      <input
        type="text"
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
        data-testid={`doc-intel-field-${name}`}
      />
    </label>
  );
}

function fieldLabel(k) {
  return k.replace(/_/g, " ").replace(/\bid\b/i, "ID");
}
